import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  padding: 2rem;
  border-top: 6px solid #ff9500;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: ${slideUp} 0.25s ease-out;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const WarningIcon = styled.div`
  font-size: 2rem;
`;

const Title = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.4rem;
`;

const FromLine = styled.p`
  margin: 0 0 1rem 0;
  color: var(--clr-sec);
  opacity: 0.8;
  font-size: 0.9rem;
`;

const ReasonBox = styled.div`
  background-color: var(--clr-bg);
  padding: 1rem;
  border-radius: 8px;
  color: var(--clr-sec);
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CountLine = styled.p`
  margin: 0 0 1.5rem 0;
  color: #ff9500;
  font-size: 0.9rem;
  font-weight: 600;
`;

const AcknowledgeButton = styled.button`
  width: 100%;
  padding: 0.85rem;
  background-color: #ff9500;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: #e68600;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * Pop-up shown to a user when a moderator sends them a warning.
 * The user must click "I understand" to dismiss it so they can't
 * claim they didn't see it.
 */
const ModWarningPopupModal = ({ warning, onClose }) => {
  const [acknowledging, setAcknowledging] = useState(false);

  if (!warning) return null;

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (token && warning.id) {
        await fetch(`${API_URL}mod/warn/${warning.id}/acknowledge`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (err) {
      // Non-fatal — still close the modal so the user isn't stuck
      console.error('Failed to acknowledge warning:', err);
    } finally {
      setAcknowledging(false);
      onClose();
    }
  };

  return (
    <Backdrop>
      <ModalContent>
        <IconRow>
          <WarningIcon>⚠️</WarningIcon>
          <Title>Warning from a moderator</Title>
        </IconRow>
        {warning.modNickname && (
          <FromLine>From: <strong>{warning.modNickname}</strong></FromLine>
        )}
        <ReasonBox>{warning.reason}</ReasonBox>
        {typeof warning.warningCount === 'number' && (
          <CountLine>
            This is warning #{warning.warningCount} on your account.
          </CountLine>
        )}
        <AcknowledgeButton onClick={handleAcknowledge} disabled={acknowledging}>
          {acknowledging ? 'Dismissing...' : 'I understand'}
        </AcknowledgeButton>
      </ModalContent>
    </Backdrop>
  );
};

export default ModWarningPopupModal;
